import type { CountryOption } from '@src/shared/lib';
import AsyncAutocompleteInput from './AsyncAutocompleteInput';

type CountryValue = CountryOption | string | number | null | undefined;

export function getCountryOptionLabel(option: CountryOption) {
  return option.ctry_name || option.ctry_short || '';
}

export function resolveCountryOption(value: CountryValue, options: CountryOption[]) {
  if (!value) {
    return null;
  }

  if (typeof value === 'object') {
    return value;
  }

  return options.find((option) => String(option.id) === String(value)) ?? null;
}

export function findCountryByCountryCode(countryCode: string | null | undefined, options: CountryOption[]) {
  const normalizedCountryCode = countryCode?.trim().toUpperCase();
  if (!normalizedCountryCode) {
    return null;
  }

  return (
    options.find((option) => option.ctry_short?.toUpperCase() === normalizedCountryCode)
    ?? options.find((option) => option.ctry_alpha3?.toUpperCase() === normalizedCountryCode)
    ?? null
  );
}

type CountryAutocompleteInputProps = {
  value: CountryValue;
  options: CountryOption[];
  label: string;
  placeholder: string;
  loading?: boolean;
  disabled?: boolean;
  onChange: (value: CountryOption | null) => void;
};

function CountryAutocompleteInput({
  value,
  options,
  label,
  placeholder,
  loading = false,
  disabled = false,
  onChange,
}: CountryAutocompleteInputProps) {
  return (
    <AsyncAutocompleteInput<CountryOption>
      value={resolveCountryOption(value, options)}
      options={options}
      label={label}
      placeholder={placeholder}
      loading={loading}
      disabled={disabled}
      valuePropName="id"
      getOptionLabel={getCountryOptionLabel}
      isOptionEqualToValue={(option, selectedValue) => String(option.id) === String(selectedValue.id)}
      onChange={onChange}
      fullWidth
    />
  );
}

export default CountryAutocompleteInput;
